/**
 * Skill Store — single source of truth for user's skill data.
 *
 * Both useSkills (task layout) and useSkillManagement (skills page) read from here.
 * Socket events and CRUD operations call fetchMySkills() to refresh.
 *
 * Backend: GET /api/items/skill/mine?limit=&cursor=[&search=&category=] — the
 * canonical Items endpoint, paginated by keyset cursor. `search` and `category`
 * are applied by the server so a filtered page is a real page; `provider` has
 * no server column and stays a best-effort match over the loaded rows.
 *
 * The first page also carries `slugs`: every skill slug the user owns, which
 * is what `installedSlugs` (the library "Installed" badge) needs — a page can
 * never answer a membership question, so the server answers it once per reset.
 *
 * This store maps the canonical Item summary down to the legacy SkillSummary
 * shape so downstream consumers (SkillsPage, useSkills, useSkillManagement)
 * don't need to change.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { SkillSummary } from "@bash/shared";
import { resilientFetch } from "@/lib/fetch";
import { SYSTEM_SKILL_SLUGS } from "@/lib/composio-tools";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

export interface SkillFilterParams {
  search?: string;
  category?: string;
  provider?: string;
  reset?: boolean; // true (default) = first page, replace; false = next page, append
}

interface SkillStore {
  mySkills: SkillSummary[];
  installedSlugs: Set<string>;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  total: number;

  // Internal
  _authToken: string | null;
  _hasFetched: boolean;
  _requestGen: number;
  _nextCursor: string | null;
  _fetchingMore: boolean;
  _lastFilters: Omit<SkillFilterParams, "reset"> | null;

  // Actions
  setAuthToken: (token: string) => void;
  fetchMySkills: (params?: SkillFilterParams) => Promise<void>;
  loadMore: () => Promise<void>;
  invalidate: () => void;
  // Optimistic install/uninstall — flip `installedSlugs` instantly so the pill
  // updates on click; the caller reverts on a failed POST and the
  // SKILL_INSTALLED/SKILL_UNINSTALLED socket reconciles mySkills.
  markInstalled: (slug: string) => void;
  markUninstalled: (slug: string) => void;
}

export const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Item → SkillSummary projection
//
// The canonical Item shape carries display fields in a `metadata` sub-object;
// SkillSummary is flat. Project across with sensible fallbacks so callers
// never have to special-case missing fields.
// ---------------------------------------------------------------------------
interface ItemResponseRow {
  id: string;
  slug: string;
  scope: "system" | "marketplace" | "user";
  metadata?: {
    name?: string;
    description?: string;
    category?: string;
    icon?: string;
    color?: string;
    mcps?: string[];
  } | null;
}

/** Wire shape of a paginated `/mine` page. `total` and `slugs` ride the first page only. */
interface MinePage {
  items?: ItemResponseRow[];
  next_cursor?: string | null;
  total?: number | null;
  slugs?: string[] | null;
}

function itemToSkillSummary(item: ItemResponseRow): SkillSummary {
  const meta = item.metadata ?? {};
  return {
    slug: item.slug,
    name: meta.name ?? item.slug,
    description: meta.description ?? "",
    category: meta.category ?? "general",
    icon: meta.icon,
    color: meta.color,
    mcps: meta.mcps ?? [],
    // Items API emits 'system' | 'marketplace' | 'user'; SkillSummary also
    // allows 'project' which the Items API does not. The narrower set is a
    // strict subset, so the assignment is safe.
    scope: item.scope,
  };
}

// `provider` was a Composio-namespace filter on the dead /summaries endpoint.
// The canonical Item carries no provider; best-effort match against mcps[].
function matchesProvider(
  s: SkillSummary,
  provider: string | undefined
): boolean {
  if (!provider) return true;
  const needle = provider.toLowerCase();
  return (s.mcps ?? []).some((m) => m.toLowerCase().includes(needle));
}

function systemFirst(list: SkillSummary[]): SkillSummary[] {
  return [...list].sort((a, b) => {
    const aS = SYSTEM_SKILL_SLUGS.has(a.slug) ? 0 : 1;
    const bS = SYSTEM_SKILL_SLUGS.has(b.slug) ? 0 : 1;
    return aS - bS;
  });
}

function pageUrl(
  filters: Omit<SkillFilterParams, "reset">,
  cursor: string | null
): string {
  const q = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (filters.search) q.set("search", filters.search);
  if (filters.category) q.set("category", filters.category);
  if (cursor) q.set("cursor", cursor);
  return `${SERVER_URL}/api/items/skill/mine?${q.toString()}`;
}

export const useSkillStore = create<SkillStore>()(
  devtools(
    (set, get) => ({
      mySkills: [],
      installedSlugs: new Set<string>(),
      loading: false,
      error: null,
      hasMore: false,
      total: 0,
      _authToken: null,
      _hasFetched: false,
      _requestGen: 0,
      _nextCursor: null,
      _fetchingMore: false,
      _lastFilters: null,

      setAuthToken: (token: string) => {
        const current = get()._authToken;
        if (current !== token) {
          set({ _authToken: token });
        }
      },

      fetchMySkills: async (params?: SkillFilterParams) => {
        const { _authToken } = get();
        if (!_authToken) return;

        const reset = params?.reset !== false; // default true

        // Generation counter for stale request prevention
        const gen = get()._requestGen + 1;
        set({ _requestGen: gen });

        // Save filters (without reset flag) for the next page to reuse
        const { reset: _r, ...filterOnly } = params || {};
        const filters = reset ? filterOnly : get()._lastFilters || {};
        const cursor = reset ? null : get()._nextCursor;
        if (reset) {
          set({
            _lastFilters:
              Object.keys(filterOnly).length > 0 ? filterOnly : null,
            _nextCursor: null,
          });
        } else {
          if (!cursor) return; // no next page
          set({ _fetchingMore: true });
        }

        // Only show loading spinner on first fetch (not on load-more)
        if (!get()._hasFetched && reset) {
          set({ loading: true });
        }

        try {
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${_authToken}`,
          };

          const response = await resilientFetch(pageUrl(filters, cursor), {
            headers,
          });

          // Stale response — a newer request was fired while this was in-flight
          if (gen !== get()._requestGen) return;

          if (!response.ok) {
            throw new Error(`Failed to fetch skills: ${response.statusText}`);
          }

          const page: MinePage = await response.json();
          const rows = (page.items || [])
            .map(itemToSkillSummary)
            .filter((s) => matchesProvider(s, filters.provider));
          const merged = systemFirst(
            reset ? rows : [...get().mySkills, ...rows]
          );
          const nextCursor = page.next_cursor ?? null;

          if (gen === get()._requestGen) {
            set({
              mySkills: merged,
              // Membership comes from the server on the first page; later pages
              // keep the set (optimistic marks included).
              installedSlugs: page.slugs
                ? new Set(page.slugs)
                : get().installedSlugs,
              hasMore: nextCursor != null,
              total: page.total ?? (reset ? merged.length : get().total),
              _nextCursor: nextCursor,
              _fetchingMore: false,
              error: null,
              _hasFetched: true,
              loading: false,
            });
          }
        } catch (err) {
          if (gen === get()._requestGen) {
            set({
              error:
                err instanceof Error ? err.message : "Failed to fetch skills",
              loading: false,
              _fetchingMore: false,
            });
          }
        }
      },

      loadMore: async () => {
        const { hasMore, loading, _fetchingMore } = get();
        if (!hasMore || loading || _fetchingMore) return;
        await get().fetchMySkills({ reset: false });
      },

      invalidate: () => {
        set({ _hasFetched: false });
      },

      markInstalled: (slug) => {
        set((s) => {
          if (s.installedSlugs.has(slug)) return {};
          const next = new Set(s.installedSlugs);
          next.add(slug);
          return { installedSlugs: next };
        });
      },

      markUninstalled: (slug) => {
        set((s) => {
          if (!s.installedSlugs.has(slug)) return {};
          const next = new Set(s.installedSlugs);
          next.delete(slug);
          return { installedSlugs: next };
        });
      },
    }),
    { name: "skill-store" }
  )
);
